import React, { useState, useEffect, useRef } from 'react';
import { Mail, Phone, Video, Send, Search, Users, Building2, Shield, User, ExternalLink } from 'lucide-react';
import { internalContacts, externalContacts } from '../data/contactsData';
import './Communications.css';

const Communications = ({ currentUser }) => {
  const [activeTab, setActiveTab] = useState('contacts');
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState({});
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const messagesEndRef = useRef(null);

  // Load messages from localStorage on mount
  useEffect(() => {
    const savedMessages = localStorage.getItem('portal_messages');
    if (savedMessages) {
      setMessages(JSON.parse(savedMessages));
    }
  }, []);

  // Save messages to localStorage whenever they change
  useEffect(() => {
    if (Object.keys(messages).length > 0) {
      localStorage.setItem('portal_messages', JSON.stringify(messages));
    }
  }, [messages]);

  // Auto-scroll to bottom of messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, selectedContact]);

  const sendMessage = () => {
    if (!messageInput.trim() || !selectedContact) return;

    const contactKey = selectedContact.email || selectedContact.id;
    const newMessage = {
      id: Date.now(),
      from: currentUser?.email || 'user@agwesternrail.com',
      fromName: currentUser?.name || 'You',
      to: contactKey,
      text: messageInput,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => ({
      ...prev,
      [contactKey]: [...(prev[contactKey] || []), newMessage]
    }));

    setMessageInput('');
  };

  const startVideoCall = (contact) => {
    // Open Google Meet
    window.open('https://meet.google.com/new', '_blank');
  };

  const sendEmail = (email) => {
    window.location.href = `mailto:${email}`;
  };

  const filteredInternalContacts = internalContacts.filter(contact =>
    contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contact.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contact.position.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredExternalContacts = externalContacts.filter(contact =>
    contact.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    contact.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getContactMessages = (contact) => {
    const contactKey = contact.email || contact.id;
    return messages[contactKey] || [];
  };

  return (
    <div className="communications-container">
      {/* Tab Navigation */}
      <div className="comm-tabs">
        <button
          className={`comm-tab ${activeTab === 'contacts' ? 'active' : ''}`}
          onClick={() => setActiveTab('contacts')}
        >
          <Users size={18} />
          Contacts
        </button>
        <button
          className={`comm-tab ${activeTab === 'messages' ? 'active' : ''}`}
          onClick={() => setActiveTab('messages')}
        >
          <Mail size={18} />
          Messages
        </button>
        <button
          className={`comm-tab ${activeTab === 'partners' ? 'active' : ''}`}
          onClick={() => setActiveTab('partners')}
        >
          <Building2 size={18} />
          External Partners
        </button>
      </div>

      {/* Search Bar */}
      <div className="comm-search">
        <Search size={18} />
        <input
          type="text"
          placeholder="Search contacts..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Content Area */}
      <div className="comm-content">
        {/* CONTACTS TAB */}
        {activeTab === 'contacts' && (
          <div className="contacts-grid">
            <h3 className="section-title">
              <Shield size={20} />
              AG&WESTERN Rail Team
            </h3>
            {filteredInternalContacts.map(contact => (
              <div key={contact.id} className="contact-card">
                <div className="contact-avatar">
                  <User size={32} />
                </div>
                <div className="contact-info">
                  <h4>{contact.name}</h4>
                  <p className="contact-position">{contact.position}</p>
                  <p className="contact-email">{contact.email}</p>
                  {contact.phone && <p className="contact-phone">{contact.phone}</p>}
                </div>
                <div className="contact-actions">
                  <button
                    className="action-btn"
                    onClick={() => sendEmail(contact.email)}
                    title="Send Email"
                  >
                    <Mail size={18} />
                  </button>
                  <button
                    className="action-btn"
                    onClick={() => {
                      setSelectedContact(contact);
                      setActiveTab('messages');
                    }}
                    title="Send Message"
                  >
                    <Send size={18} />
                  </button>
                  <button
                    className="action-btn"
                    onClick={() => startVideoCall(contact)}
                    title="Start Video Call"
                  >
                    <Video size={18} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* MESSAGES TAB */}
        {activeTab === 'messages' && (
          <div className="messages-layout">
            <div className="message-sidebar">
              <h4>Recent Conversations</h4>
              {internalContacts
                .filter(contact => contact.email !== currentUser?.email) // Don't show current user
                .map(contact => {
                const contactMessages = getContactMessages(contact);
                const hasMessages = contactMessages.length > 0;
                return (
                  <div
                    key={contact.id}
                    className={`message-contact-item ${selectedContact?.id === contact.id ? 'active' : ''} ${hasMessages ? 'has-messages' : ''}`}
                    onClick={() => setSelectedContact(contact)}
                  >
                    <User size={24} />
                    <div>
                      <div className="message-contact-name">{contact.name}</div>
                      <div className="message-contact-preview">
                        {hasMessages ? `${contactMessages.length} messages` : 'No messages'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="message-main">
              {selectedContact ? (
                <>
                  <div className="message-header">
                    <User size={32} />
                    <div>
                      <h4>{selectedContact.name}</h4>
                      <p>{selectedContact.position}</p>
                    </div>
                    <div className="message-header-actions">
                      <button onClick={() => startVideoCall(selectedContact)}>
                        <Video size={18} /> Start Call
                      </button>
                    </div>
                  </div>

                  <div className="message-thread">
                    {getContactMessages(selectedContact).map(msg => {
                      const isSent = msg.from === currentUser?.email;
                      const displayName = isSent ? 'You' : selectedContact.name;
                      return (
                        <div
                          key={msg.id}
                          className={`message-bubble ${isSent ? 'sent' : 'received'}`}
                        >
                          <div className="message-sender">{displayName}</div>
                          <div className="message-text">{msg.text}</div>
                          <div className="message-time">
                            {new Date(msg.timestamp).toLocaleString()}
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="message-input-container">
                    <input
                      type="text"
                      placeholder={`Message ${selectedContact.name}...`}
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                    />
                    <button onClick={sendMessage} disabled={!messageInput.trim()}>
                      <Send size={18} />
                    </button>
                  </div>
                </>
              ) : (
                <div className="message-empty-state">
                  <Mail size={48} />
                  <h3>Select a contact to start messaging</h3>
                  <p>Send secure internal messages to your colleagues</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* EXTERNAL PARTNERS TAB */}
        {activeTab === 'partners' && (
          <div className="partners-grid">
            <h3 className="section-title">
              <Building2 size={20} />
              External Partners & Agencies
            </h3>
            {filteredExternalContacts.map(partner => (
              <div key={partner.id} className="partner-card">
                <div className="partner-logo">
                  <img src={partner.logo} alt={`${partner.name} logo`} />
                </div>
                <div className="partner-info">
                  <h4>{partner.name}</h4>
                  <p className="partner-type">{partner.type}</p>
                  {partner.description && <p className="partner-desc">{partner.description}</p>}
                  
                  {partner.email && (
                    <div className="partner-contact-item">
                      <Mail size={16} />
                      <a href={`mailto:${partner.email}`}>{partner.email}</a>
                    </div>
                  )}
                  
                  {partner.phone && (
                    <div className="partner-contact-item">
                      <Phone size={16} />
                      <a href={`tel:${partner.phone}`}>{partner.phone}</a>
                    </div>
                  )}

                  {partner.contacts && partner.contacts.map((contactInfo, idx) => (
                    <div key={idx} className="partner-contact-section">
                      <strong>{contactInfo.label}</strong>
                      {contactInfo.phone && (
                        <div className="partner-contact-item">
                          <Phone size={16} />
                          <a href={`tel:${contactInfo.phone}`} className={contactInfo.emergency ? 'emergency' : ''}>
                            {contactInfo.phone}
                            {contactInfo.emergency && ' (Emergency)'}
                          </a>
                        </div>
                      )}
                      {contactInfo.address && (
                        <p className="partner-address">{contactInfo.address}</p>
                      )}
                    </div>
                  ))}

                  {partner.website && (
                    <div className="partner-contact-item">
                      <ExternalLink size={16} />
                      <a href={partner.website} target="_blank" rel="noopener noreferrer">
                        Visit Website
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Communications;
